import { property, customElement } from 'lit/decorators.js';
import { s as styles$1, b as bound, S as StripeBase, d as dash } from './shared.js';
import { css } from 'lit';
import 'lit/directive.js';
import 'lit/directives/if-defined.js';
import '@typed/curry';
import '@lavadrop/kebab-case';
import '@lavadrop/camel-case';
import '@pacote/memoize';

/* eslint-disable @typescript-eslint/ban-types, no-invalid-this, @typescript-eslint/no-explicit-any */
function wrap(f) {
    return (_target, _property, descriptor) => {
        const original = descriptor.value;
        descriptor.value = f(original);
        return descriptor;
    };
}
const stripeMethod = wrap(function (f) {
    const { name } = f;
    return async function (...args) {
        if (!this.stripe)
            throw new Error(`<${this.constructor.is}>: Stripe must be initialized before calling ${name}.`);
        return f.call(this, ...args)
            .then(this.handleResponse);
    };
});

const styles = css`:host {
  min-width: var(--stripe-elements-width, 300px);
  min-height: var(--stripe-elements-height, 50px);
}

#stripe {
  box-sizing: border-box;
  border-radius: var(--stripe-elements-border-radius, 4px);
  border: var(--stripe-elements-border, 1px solid transparent);
  box-shadow: var(--stripe-elements-box-shadow, 0 1px 3px 0 #e6ebf1);
  transition: var(--stripe-elements-transition, box-shadow 150ms ease);
  min-width: var(--stripe-elements-width, 300px);
  padding: var(--stripe-elements-element-padding, 8px 12px);
  background: var(--stripe-elements-element-background, white);
}

:host([focused]) #stripe {
  box-shadow: 0 1px 3px 0 #cfd7df;
}

:host([error]) #stripe {
  border-color: #fa755a;
}
`;

var __decorate = (undefined && undefined.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (undefined && undefined.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
const allowedStyles = [
    'color',
    'fontFamily',
    'fontSize',
    'fontStyle',
    'fontSmoothing',
    'fontVariant',
    'iconColor',
    'lineHeight',
    'letterSpacing',
    'textDecoration',
    'textShadow',
    'textTransform',
];
/**
 * [Stripe.js v3 Card Elements](https://stripe.com/docs/elements), but it's a Web Component!
 * Supports Shadow DOM.
 *
 * 👨‍🎨 [Live Demo](https://bennypowers.dev/stripe-elements/?path=/docs/stripe-elements--enter-a-stripe-publishable-key) 👀
 *
 * ### 🧙‍♂️ Usage
 * If you prebuilt with Snowpack, load the module from your `web_modules` directory
 *
 * ```html
 * <script type="module" src="/web_modules/@power-elements/stripe-elements/stripe-elements.js"></script>
 * ```
 *
 * Alternatively, load the module from the unpkg CDN
 * ```html
 * <script type="module" src="https://unpkg.com/@power-elements/stripe-elements/stripe-elements.js?module"></script>
 * ```
 *
 * Then you can add the element to your page.
 *
 * ```html
 * <stripe-elements id="stripe"
 *     action="/payment"
 *     publishable-key="pk_test_XXXXXXXXXXXXXXXXXXXXXXXX"
 * ></stripe-elements>
 * ```
 *
 * See the demos for more comprehensive examples.
 *   - Using `<stripe-elements>` with [plain HTML and JavaScript](https://bennypowers.dev/stripe-elements/?path=/docs/framework-examples-html--stripe-elements).
 *   - Using `<stripe-elements>` in a [LitElement](https://bennypowers.dev/stripe-elements/?path=/docs/framework-examples-litelement--stripe-elements).
 *   - Using `<stripe-elements>` in a [Vue Component](https://bennypowers.dev/stripe-elements/?path=/docs/framework-examples-vue--stripe-elements).
 *   - Using `<stripe-elements>` in an [Angular component](https://bennypowers.dev/stripe-elements/?path=/docs/framework-examples-angular--stripe-elements).
 *   - Using `<stripe-elements>` in a [React component](https://bennypowers.dev/stripe-elements/?path=/docs/framework-examples-react--stripe-elements).
 *   - Using `<stripe-elements>` in a [Preact component](https://bennypowers.dev/stripe-elements/?path=/docs/framework-examples-preact--stripe-elements).
 *
 * ## Styling
 *
 * Stripe v3's 'Stripe Elements' are not custom elements, but rather forms
 * hosted by stripe and injected into your page via an iFrame. When we refer to the
 * 'Stripe Element' in this document, we are referring to the hosted Stripe form,
 * not the `<stripe-element>` custom element. But when I mention the 'element', I mean the custom element.
 *
 * When you apply CSS to the custom properties available, they're parsed and sent to Stripe, who should apply them to the Stripe Element they return in the iFrame.
 *
 * - `base` styles are inherited by all other variants.
 * - `complete` styles are applied when the Stripe Element has valid input.
 * - `empty` styles are applied when the Stripe Element has no user input.
 * - `invalid` styles are applied when the Stripe Element has invalid input.
 *
 * There are 11 properties for each state that you can set which will be read into the Stripe Element iFrame:
 *
 * - `color`
 * - `font-family`
 * - `font-size`
 * - `font-smoothing`
 * - `font-variant`
 * - `icon-color`
 * - `line-height`
 * - `letter-spacing`
 * - `text-decoration`
 * - `text-shadow`
 * - `text-transform`
 *
 * @cssprop [--stripe-elements-border-radius=`4px`] - border radius of the element container
 * @cssprop [--stripe-elements-border=`1px solid transparent`] - border property of the element container
 * @cssprop [--stripe-elements-box-shadow=`0 1px 3px 0 #e6ebf1`] - box shadow for the element container
 * @cssprop [--stripe-elements-transition=`box-shadow 150ms ease`] - transition property for the element container
 *
 * @cssprop [--stripe-elements-base-color] - `color` property for the element in its base state
 * @cssprop [--stripe-elements-base-font-family] - `font-family` property for the element in its base state
 * @cssprop [--stripe-elements-base-font-size] - `font-size` property for the element in its base state
 * @cssprop [--stripe-elements-base-font-smoothing] - `font-smoothing` property for the element in its base state
 * @cssprop [--stripe-elements-base-font-variant] - `font-variant` property for the element in its base state
 * @cssprop [--stripe-elements-base-icon-color] - `icon-color` property for the element in its base state
 * @cssprop [--stripe-elements-base-line-height] - `line-height` property for the element in its base state
 * @cssprop [--stripe-elements-base-letter-spacing] - `letter-spacing` property for the element in its base state
 * @cssprop [--stripe-elements-base-text-decoration] - `text-decoration` property for the element in its base state
 * @cssprop [--stripe-elements-base-text-shadow] - `text-shadow` property for the element in its base state
 * @cssprop [--stripe-elements-base-text-transform] - `text-transform` property for the element in its base state
 *
 * @cssprop [--stripe-elements-complete-color] - `color` property for the element in its complete state
 * @cssprop [--stripe-elements-complete-font-family] - `font-family` property for the element in its complete state
 * @cssprop [--stripe-elements-complete-font-size] - `font-size` property for the element in its complete state
 * @cssprop [--stripe-elements-complete-font-smoothing] - `font-smoothing` property for the element in its complete state
 * @cssprop [--stripe-elements-complete-font-variant] - `font-variant` property for the element in its complete state
 * @cssprop [--stripe-elements-complete-icon-color] - `icon-color` property for the element in its complete state
 * @cssprop [--stripe-elements-complete-line-height] - `line-height` property for the element in its complete state
 * @cssprop [--stripe-elements-complete-letter-spacing] - `letter-spacing` property for the element in its complete state
 * @cssprop [--stripe-elements-complete-text-decoration] - `text-decoration` property for the element in its complete state
 * @cssprop [--stripe-elements-complete-text-shadow] - `text-shadow` property for the element in its complete state
 * @cssprop [--stripe-elements-complete-text-transform] - `text-transform` property for the element in its complete state
 *
 * @cssprop [--stripe-elements-empty-color] - `color` property for the element in its empty state
 * @cssprop [--stripe-elements-empty-font-family] - `font-family` property for the element in its empty state
 * @cssprop [--stripe-elements-empty-font-size] - `font-size` property for the element in its empty state
 * @cssprop [--stripe-elements-empty-font-smoothing] - `font-smoothing` property for the element in its empty state
 * @cssprop [--stripe-elements-empty-font-variant] - `font-variant` property for the element in its empty state
 * @cssprop [--stripe-elements-empty-icon-color] - `icon-color` property for the element in its empty state
 * @cssprop [--stripe-elements-empty-line-height] - `line-height` property for the element in its empty state
 * @cssprop [--stripe-elements-empty-letter-spacing] - `letter-spacing` property for the element in its empty state
 * @cssprop [--stripe-elements-empty-text-decoration] - `text-decoration` property for the element in its empty state
 * @cssprop [--stripe-elements-empty-text-shadow] - `text-shadow` property for the element in its empty state
 * @cssprop [--stripe-elements-empty-text-transform] - `text-transform` property for the element in its empty state
 *
 * @cssprop [--stripe-elements-invalid-color] - `color` property for the element in its invalid state
 * @cssprop [--stripe-elements-invalid-font-family] - `font-family` property for the element in its invalid state
 * @cssprop [--stripe-elements-invalid-font-size] - `font-size` property for the element in its invalid state
 * @cssprop [--stripe-elements-invalid-font-smoothing] - `font-smoothing` property for the element in its invalid state
 * @cssprop [--stripe-elements-invalid-font-variant] - `font-variant` property for the element in its invalid state
 * @cssprop [--stripe-elements-invalid-icon-color] - `icon-color` property for the element in its invalid state
 * @cssprop [--stripe-elements-invalid-line-height] - `line-height` property for the element in its invalid state
 * @cssprop [--stripe-elements-invalid-letter-spacing] - `letter-spacing` property for the element in its invalid state
 * @cssprop [--stripe-elements-invalid-text-decoration] - `text-decoration` property for the element in its invalid state
 * @cssprop [--stripe-elements-invalid-text-shadow] - `text-shadow` property for the element in its invalid state
 * @cssprop [--stripe-elements-invalid-text-transform] - `text-transform` property for the element in its invalid state
 *
 * @element stripe-elements
 * @extends StripeBase
 *
 * @fires 'change' - Stripe Element change event
 */
let StripeElements = class StripeElements extends StripeBase {
    constructor() {
        super(...arguments);
        /* PUBLIC FIELDS */
        /**
         * Whether to hide icons in the Stripe form.
         */
        this.hideIcon = false;
        /**
         * Whether or not to hide the postal code field.
         * Useful when you gather shipping info elsewhere.
         */
        this.hidePostalCode = false;
        /**
         * Stripe icon style.
         */
        this.iconStyle = 'default';
        /**
         * Prefilled values for form.
         * @example { postalCode: '90210' }
         */
        this.value = {};
        /* READ ONLY PROPERTIES */
        /**
         * The card brand detected by Stripe
         */
        this.brand = null;
        /**
         * Whether the form is complete.
         */
        this.complete = false;
        /**
         * If the form is empty.
         */
        this.empty = true;
        /**
         * Whether the form is invalid.
         */
        this.invalid = false;
        // DEPRECATED
        /**
         * The Stripe card object.
         * **DEPRECATED**. Will be removed in a future version. use `element` instead
         * @deprecated
         */
        this.card = null;
        /**
         * Whether the form is empty.
         * **DEPRECATED**. Will be removed in a future version. use `empty` instead
         * @deprecated
         */
        this.isEmpty = true;
        /**
         * Whether the form is complete.
         * **DEPRECATED**. Will be removed in a future version. use `complete` instead
         * @deprecated
         */
        this.isComplete = false;
    }
    updated(changed) {
        super.updated(changed);
        // DEPRECATED
        if (changed.has('element') && !this.element)
            this.setReadOnlyProperties({ card: null });
    }
    /* PUBLIC API */
    /**
     * Submit payment information to generate a paymentMethod
     */
    async createPaymentMethod(paymentMethodData = this.getPaymentMethodData()) {
        return this.stripe.createPaymentMethod(paymentMethodData);
    }
    /**
     * Submit payment information to generate a source
     */
    async createSource(sourceData = this.sourceData) {
        return this.stripe.createSource(this.element, sourceData);
    }
    /**
     * Submit payment information to generate a token
     */
    async createToken(tokenData = this.tokenData) {
        return this.stripe.createToken(this.element, tokenData);
    }
    /**
     * Checks for potential validity. A potentially valid form is one that is not empty, not complete and has no error. A validated form also counts as potentially valid.
     */
    isPotentiallyValid() {
        return (!this.complete && !this.empty && !this.error) || this.validate();
    }
    /**
     * Resets the Stripe card.
     */
    reset() {
        var _a;
        super.reset();
        /* istanbul ignore next */
        (_a = this.element) === null || _a === void 0 ? void 0 : _a.clear();
    }
    /**
     * Generates a payment representation of the type specified by `generate`.
     */
    async submit() {
        switch (this.generate) {
            case 'payment-method': return this.createPaymentMethod();
            case 'source': return this.createSource();
            case 'token': return this.createToken();
            default: {
                const error = this.createError(`cannot generate ${this.generate}`);
                await this.setReadOnlyProperties({ error });
                throw error;
            }
        }
    }
    /**
     * Checks if the Stripe form is valid.
     */
    validate() {
        const { complete, empty, error } = this;
        const isValid = !error && complete && !empty;
        if (empty && !error)
            this.setReadOnlyProperties({ error: this.createError('Your card number is empty.') });
        return isValid;
    }
    /* PRIVATE METHODS */
    /**
     * Generates PaymentMethodData from the element.
     */
    getPaymentMethodData() {
        const type = 'card';
        const { billingDetails, element: card, paymentMethodData } = this;
        return ({
            billing_details: billingDetails,
            ...paymentMethodData,
            type,
            card,
        });
    }
    /**
     * Returns a Stripe-friendly style object computed from CSS custom properties
     */
    getStripeElementsStyles() {
        const computedStyle = window.ShadyCSS ? undefined : getComputedStyle(this);
        const getStyle = (prop) => this.getCSSCustomPropertyValue(prop, computedStyle) || undefined;
        const styleReducer = ({ base = {}, complete = {}, empty = {}, invalid = {}, }, camelCase) => ({
            base: { ...base, [camelCase]: getStyle(`--stripe-elements-base-${dash(camelCase)}`) },
            complete: { ...complete, [camelCase]: getStyle(`--stripe-elements-complete-${dash(camelCase)}`) },
            empty: { ...empty, [camelCase]: getStyle(`--stripe-elements-empty-${dash(camelCase)}`) },
            invalid: { ...invalid, [camelCase]: getStyle(`--stripe-elements-invalid-${dash(camelCase)}`) },
        });
        return allowedStyles.reduce(styleReducer, {});
    }
    async initElement() {
        if (!this.stripe)
            return;
        const { hidePostalCode, hideIcon, iconStyle, value } = this;
        const style = this.getStripeElementsStyles();
        const options = {
            hideIcon,
            hidePostalCode,
            iconStyle,
            style,
            value,
        };
        const element = this.elements.create('card', options);
        element.addEventListener('change', this.onChange);
        await this.setReadOnlyProperties({
            element,
            // DEPRECATED
            card: element,
        });
    }
    /**
     * Updates the element's state.
     */
    async onChange(event) {
        const { brand, complete, empty, error = null } = event;
        const invalid = error || (!empty && !complete);
        await this.setReadOnlyProperties({
            brand,
            complete,
            empty,
            error,
            invalid,
            // DEPRECATED
            isComplete: complete,
            isEmpty: empty,
        });
        this.fire('change', event);
        // DEPRECATED
        this.fire('stripe-change', event);
    }
};
StripeElements.is = 'stripe-elements';
StripeElements.elementType = 'card';
StripeElements.styles = [
    styles$1,
    styles,
];
__decorate([
    slot,
    __metadata("design:type", String)
], StripeElements.prototype, "slotName", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-icon' }),
    __metadata("design:type", Object)
], StripeElements.prototype, "hideIcon", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-postal-code' }),
    __metadata("design:type", Object)
], StripeElements.prototype, "hidePostalCode", void 0);
__decorate([
    property({ type: String, attribute: 'icon-style' }),
    __metadata("design:type", Object)
], StripeElements.prototype, "iconStyle", void 0);
__decorate([
    property({ type: Object }),
    __metadata("design:type", Object)
], StripeElements.prototype, "value", void 0);
__decorate([
    property({ type: String, notify: true, readOnly: true }),
    __metadata("design:type", String)
], StripeElements.prototype, "brand", void 0);
__decorate([
    property({ type: Boolean, reflect: true, notify: true, readOnly: true }),
    __metadata("design:type", Object)
], StripeElements.prototype, "complete", void 0);
__decorate([
    property({ type: Boolean, reflect: true, notify: true, readOnly: true }),
    __metadata("design:type", Object)
], StripeElements.prototype, "empty", void 0);
__decorate([
    property({ type: Boolean, reflect: true, notify: true, readOnly: true }),
    __metadata("design:type", Object)
], StripeElements.prototype, "invalid", void 0);
__decorate([
    property({ type: Object, notify: true, readOnly: true }),
    __metadata("design:type", Object)
], StripeElements.prototype, "card", void 0);
__decorate([
    property({ type: Boolean, attribute: 'is-empty', reflect: true, notify: true, readOnly: true }),
    __metadata("design:type", Object)
], StripeElements.prototype, "isEmpty", void 0);
__decorate([
    property({
        type: Boolean,
        attribute: 'is-complete',
        reflect: true,
        notify: true,
        readOnly: true,
    }),
    __metadata("design:type", Object)
], StripeElements.prototype, "isComplete", void 0);
__decorate([
    stripeMethod,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Promise)
], StripeElements.prototype, "createPaymentMethod", null);
__decorate([
    stripeMethod,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Promise)
], StripeElements.prototype, "createSource", null);
__decorate([
    stripeMethod,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Promise)
], StripeElements.prototype, "createToken", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Promise)
], StripeElements.prototype, "onChange", null);
StripeElements = __decorate([
    customElement('stripe-elements')
], StripeElements);
/**
 * Allows narrowing class field type
 * @see https://dev.to/bennypowers/narrowing-the-type-of-class-accessors-bi8
 */
function slot(proto, key) {
    Object.defineProperty(proto, key, {
        get() { return "stripe-elements-slot" /* 'stripe-elements' */; },
    });
}

export { StripeElements };
//# sourceMappingURL=stripe-elements.js.map
